import React from 'react';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { useLocation } from '@reach/router';

type SEOProps = {
  title?: string;
  description?: string;
  image?: string;
  keywords?: string;
  isPaginated?: boolean;
  currentPage?: number;
  totalPages?: number;
  isBlogPost?: boolean;
  blogPostData?: {
    title: string;
    description: string;
    date: string;
    author: string | string[];
    image: string;
    url: string;
  };
  isAuthor?: boolean;
  authorData?: {
    name: string;
    bio: string;
    image: string;
    socialMediaProfiles: {
      key: string;
      value: string;
    }[];
    blog_post: {
      id: string;
      title: string;
      shortTitle: string;
      date: string;
      slug: string;
      featuredImage: {
        gatsbyImageData: {
          width: number;
          layout: string;
          placeholder: string;
        };
      };
      categories: string[];
    }[];
  };
};

const SEO = ({
  title,
  description,
  image,
  keywords,
  isPaginated = false,
  currentPage = 1,
  totalPages = 1,
  isBlogPost = false,
  blogPostData,
  isAuthor = false,
  authorData,
}: SEOProps) => {
  const { pathname } = useLocation();

  const { site } = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          title
          description
          url
          image
          keywords
        }
      }
    }
  `);

  const { siteMetadata } = site;

  const seo = {
    title: title,
    url: `${siteMetadata.url}${pathname || ``}`,
    keywords: keywords || siteMetadata.keywords,
    description: description || siteMetadata.description,
    image: `${image || `${siteMetadata.url}${siteMetadata.image}`}`,
  };

  function removeTrailingSlash(url) {
    const reg = /\S*\/$/g
    const result = reg.test(url);
    if (result)  {
      return url.replace(/\/$/, "")
    }
    return url
  }

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Predibase",
    "url": "https://predibase.com",
    "logo": `${siteMetadata.url}${siteMetadata.image}`,
    "foundingDate": "2021",
    "founders": [
      {
        "@type": "Person",
        "name": "Devvret Rishi"
      },
      {
        "@type": "Person",
        "name": "Travis Addair"
      },
      {
        "@type": "Person",
        "name": "Piero Molino"
      }
    ],
    "funding": {
      "@type": "MonetaryGrant",
      "amount": {
        "@type": "MonetaryAmount",
        "value": "25000000",
        "currency": "USD"
      },
      "sponsor": [
        {
          "@type": "Organization",
          "name": "Greylock"
        },
        {
          "@type": "Organization",
          "name": "Redpoint Ventures"
        }
      ]
    },
    "knowsAbout": [
      {
        "@type": "Thing",
        "name": "Large language models",
        "sameAs": "https://en.wikipedia.org/wiki/Large_language_model"
      },
      {
        "@type": "Thing",
        "name": "Fine-tuning",
        "sameAs": "https://en.wikipedia.org/wiki/Fine-tuning_(machine_learning)"
      },
      {
        "@type": "Thing",
        "name": "LoRA",
        "sameAs": "https://huggingface.co/blog/lora"
      },
      {
        "@type": "Thing",
        "name": "TurboLoRA"
      },
      {
        "@type": "Thing",
        "name": "Inference Optimization"
      },
      {
        "@type": "Thing",
        "name": "Reinforcement fine-tuning"
      }
    ],
    "keywords": "LLM, LoRA, inference, AI, fine-tuning, open-source models, AI serving",
    "location": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "San Francisco",
        "addressRegion": "CA"
      }
    },
    "sameAs": [
      "https://www.youtube.com/@Predibase",
      "https://x.com/predibase",
      "https://www.linkedin.com/company/predibase",
      "https://www.instagram.com/predibase_hq/"
    ],
    "slogan": "The fastest way to fine-tune and serve open-source LLMs",
    "award": [
      "Gartner Cool Vendor in AI Engineering",
      "CB Insights AI 100 Honoree"
    ]
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "url": siteMetadata.url,
    "name": siteMetadata.title,
    "description": siteMetadata.description,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${siteMetadata.url}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  // Calculate pagination URLs
  let prevPageUrl = null;
  let nextPageUrl = null;

  if (isPaginated) {
    // For first page
    if (currentPage === 1 && totalPages > 1) {
      nextPageUrl = `${siteMetadata.url}/blog/2`;
    }
    // For middle pages
    else if (currentPage > 1 && currentPage < totalPages) {
      prevPageUrl = currentPage === 2
        ? `${siteMetadata.url}/blog`
        : `${siteMetadata.url}/blog/${currentPage - 1}`;
      nextPageUrl = `${siteMetadata.url}/blog/${currentPage + 1}`;
    }
    // For last page
    else if (currentPage > 1 && currentPage === totalPages) {
      prevPageUrl = currentPage === 2
        ? `${siteMetadata.url}/blog`
        : `${siteMetadata.url}/blog/${currentPage - 1}`;
    }
  }

  // Create blog listing schema for paginated pages
  const blogListingSchema = isPaginated ? {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "url": seo.url,
    "isPartOf": {
      "@type": "Blog",
      "url": `${siteMetadata.url}/blog`,
      "name": `${siteMetadata.title} Blog`
    },
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": totalPages,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": currentPage,
          "url": seo.url
        }
      ]
    }
  } : null;

  // Add Article schema for blog posts
  const articleSchema = isBlogPost && blogPostData ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": blogPostData.title,
    "description": blogPostData.description,
    "image": blogPostData.image,
    "datePublished": blogPostData.date,
    "dateModified": blogPostData.date,
    "author": Array.isArray(blogPostData.author)
      ? blogPostData.author.map(author => ({
          "@type": "Person",
          "name": author,
          "url": `${siteMetadata.url}/author/${author.toLowerCase().replace(/ /g, '-')}`
        }))
      : {
          "@type": "Person",
          "name": blogPostData.author,
          "url": `${siteMetadata.url}/author/${blogPostData.author.toLowerCase().replace(/ /g, '-')}`
        },
    "publisher": {
      "@type": "Organization",
      "name": "Predibase",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteMetadata.url}${siteMetadata.image}`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": blogPostData.url
    }
  } : null;

  // Add Person schema for authors
  const authorSchema =
    isAuthor && authorData
      ? {
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: authorData.name,
          description: authorData.bio,
          image: authorData.image,
          sameAs: authorData.socialMediaProfiles?.map((profile) => profile.value) || [],
          url: `${siteMetadata.url}/author/${authorData.name?.toLowerCase().replace(/ /g, '-')}`,
          worksFor: {
            '@type': 'Organization',
            name: 'Predibase',
          },
        }
      : null;

  return (
    <Helmet htmlAttributes={{ lang: 'en' }} defaultTitle={siteMetadata.title}>
      <script
        src="https://analytics.ahrefs.com/analytics.js"
        data-key="SYaQZbha0cDmERMzbgAbNg"
        async
      ></script>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      <meta name="keywords" content={seo.keywords} />
      <meta name="language" content="en" />
      <meta name="facebook-domain-verification" content="qm6uxyd3t5xf41io1id6i0om0lyc0g" />

      {/* For paginated pages, set canonical to the first page */}
      <link
        rel="canonical"
        href={
          isPaginated && currentPage > 1
            ? `${siteMetadata.url}/blog`
            : removeTrailingSlash(seo.url)
        }
      />

      {/* Add prev/next links for pagination */}
      {prevPageUrl && <link rel="prev" href={prevPageUrl} />}
      {nextPageUrl && <link rel="next" href={nextPageUrl} />}

      <meta property="og:title" content={seo.title} />
      <meta property="og:url" content={removeTrailingSlash(seo.url)} />
      <meta property="og:type" content="website" />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />

      <meta name="twitter:site" content="@predibase" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />

      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>

      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>

      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}

      {authorSchema && (
        <script type="application/ld+json">
          {JSON.stringify(authorSchema)}
        </script>
      )}

      {/* Add blog listing schema for paginated pages */}
      {blogListingSchema && (
        <script type="application/ld+json">
          {JSON.stringify(blogListingSchema)}
        </script>
      )}
    </Helmet>
  );
};
export default SEO;
